import { useEffect, useState } from "react";
import { useKmsService } from "../providers/kms-service-provider";
import {
  CourseEstimateResult,
  TypeProduit,
  deduceTypeVehicule,
} from "../types/course-estimate";
import { CourseForm } from "../types/forms/course-form";
import { isRoutableTrip } from "../utils/course-stops";

const DEBOUNCE_MS = 800;

type UseCourseEstimateParams = {
  idService: number | undefined;
  nombresPassagers: number | undefined;
  dateCourse: string | undefined;
  dateTrajetRetour: string | null | undefined;
  avecTrajetRetour: boolean | undefined;
  arrets: CourseForm["arrets"] | undefined;
  /**
   * Faux quand la course suit un tarif négocié : son prix est celui du tarif,
   * l'appeler afficherait au client une estimation que le backend n'appliquera
   * jamais.
   */
  actif?: boolean;
};

/**
 * Estimation de prix d'une course : recalculée (avec debounce) dès que le
 * trajet, le service ou la date changent, et remise à zéro tant que le trajet
 * n'est pas exploitable.
 */
export const useCourseEstimate = ({
  idService,
  nombresPassagers,
  dateCourse,
  dateTrajetRetour,
  avecTrajetRetour,
  arrets,
  actif = true,
}: UseCourseEstimateParams) => {
  const { courseService } = useKmsService();

  const [estimate, setEstimate] = useState<CourseEstimateResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Le trajet doit être exploitable avant d'interroger le backend : le
    // formulaire démarre avec deux arrêts vides, les envoyer ne produirait
    // qu'un aller-retour inutile.
    if (
      !actif ||
      !isRoutableTrip(arrets) ||
      !idService ||
      Number(idService) === 0 ||
      !dateCourse
    ) {
      setEstimate(null);
      setError(null);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setIsLoading(true);
        setError(null);
        const result = await courseService.estimateCourse({
          idService: Number(idService),
          typeProduit: TypeProduit.Transfert,
          typeVehicule: deduceTypeVehicule(
            Number(idService),
            Number(nombresPassagers),
          ),
          nombresPassagers: Number(nombresPassagers),
          dateCourse,
          dateTrajetRetour: avecTrajetRetour
            ? (dateTrajetRetour ?? null)
            : null,
          avecTrajetRetour: !!avecTrajetRetour,
          arrets: (arrets ?? []).map((stop, index) => {
            const { rue, numero, boite, codePostal, ville, pays, placeId } =
              stop.adresse;
            const base = { rue, numero, boite, codePostal, ville, pays };

            return {
              ordre: index,
              type: stop.type,
              // N'inclure placeId que s'il existe (suggestion Google) ; sinon on
              // n'envoie que l'adresse texte, comme pour /api/adresses/itineraire.
              adresse: placeId ? { ...base, placeId } : base,
            };
          }),
        });
        setEstimate(result);
      } catch {
        setEstimate(null);
        setError("Impossible de calculer le prix pour le moment.");
      } finally {
        setIsLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // `arrets` est un tableau recréé à chaque rendu par react-hook-form : on le
    // compare par valeur pour ne relancer l'estimation qu'en cas de vrai
    // changement d'itinéraire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    courseService,
    actif,
    idService,
    nombresPassagers,
    dateCourse,
    dateTrajetRetour,
    avecTrajetRetour,
    JSON.stringify(arrets),
  ]);

  return { estimate, isLoading, error };
};
